import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getProductDetail } from "../../api/buyerProducts.api";
import {createRFQ } from "../../api/buyerRFQ.api";
import { toast } from "react-toastify";
import { useCompare } from "../../context/CompareContext";

const ComparePage = () => {
  const navigate = useNavigate();
  const { items, removeFromCompare, clearCompare } = useCompare();
  
  const [itemsWithSpecs, setItemsWithSpecs] = useState(items || []);

  // MOBILE STATE DETECTION (Laptops generally >= 992px)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const colors = {
    primary: "#112347",
    accent: "#ffca44",
    bgLight: "#f8f9fa",
    border: "#e5e7eb",
    textMuted: "#6b7280",
    success: "#059669"
  };

  const sectionHeaderStyle = {
    backgroundColor: "#eff3f9",
    color: colors.primary,
    fontWeight: "700",
    fontSize: isMobile ? "12px" : "14px",
    textAlign: "left",
    padding: isMobile ? "10px" : "12px 20px",
    position: isMobile ? "sticky" : "static",
    left: 0,
    zIndex: isMobile ? 5 : "auto"
  };

  const rowHeaderStyle = {
    textAlign: "left",
    padding: isMobile ? "12px 10px" : "15px 20px",
    fontWeight: "600",
    color: colors.primary,
    fontSize: isMobile ? "11px" : "13px",
    width: isMobile ? "110px" : "20%",
    minWidth: isMobile ? "110px" : "0",
    backgroundColor: isMobile ? "#f8fafc" : "white",
    borderRight: `1px solid ${colors.border}`,
    position: isMobile ? "sticky" : "static",
    left: 0,
    zIndex: isMobile ? 5 : "auto",
    boxShadow: isMobile ? "2px 0 5px -2px rgba(0,0,0,0.1)" : "none",
    // Force text wrapping to prevent push-outs
    wordWrap: "break-word",
    whiteSpace: "normal"
  };

  const topLeftHeaderStyle = {
    ...sectionHeaderStyle,
    fontSize: isMobile ? "12px" : "18px",
    padding: isMobile ? "12px 10px" : "12px 20px",
    width: isMobile ? "110px" : "20%",
    minWidth: isMobile ? "110px" : "0",
    position: isMobile ? "sticky" : "static",
    left: 0,
    zIndex: isMobile ? 15 : "auto",
    boxShadow: isMobile ? "2px 0 5px -2px rgba(0,0,0,0.1)" : "none",
    verticalAlign: "middle"
  };

  const dataCellStyle = {
    padding: isMobile ? "12px 10px" : "15px",
    fontSize: isMobile ? "12px" : "13px",
    color: colors.textMuted,
    backgroundColor: "white",
    textAlign: "left",
    minWidth: isMobile ? "160px" : "0",
    // Force text wrapping to prevent push-outs
    wordWrap: "break-word",
    whiteSpace: "normal"
  };

  useEffect(() => {
    const fetchSpecs = async () => {
      const updated = await Promise.all(
        items.map(async (item) => {
          const res = await getProductDetail(item.listed_product_id);
          return {
            ...item,
            specs: res?.data?.data?.specs || []
          };
        })
      );
      setItemsWithSpecs(updated);
    };

    if (items.length) fetchSpecs();
  }, [items]);

  const getAllSpecs = (items) => {
    const set = new Set();
    items.forEach(item => {
      item.specs?.forEach(s => set.add(s.spec_label));
    });
    return Array.from(set);
  };

  if (items.length === 0) {
    return <div className="p-5 text-center fw-bold">No products selected for comparison</div>;
  }

  const handleSingleRFQ = async (item) => {
    try {
      await createRFQ({
        seller_product_id: item.listed_product_id,
        quantity: 1
      });
      toast.success("RFQ sent");
    } catch (e) {
      toast.error("RFQ failed");
    }
  };

  const handleBulkRFQ = async () => {
    try {
      for (let item of itemsWithSpecs) {
        await createRFQ({
          seller_product_id: item.listed_product_id,
          quantity: 1
        });
      }
      toast.success("RFQs sent to all sellers");
    } catch (e) {
      toast.error("Error sending RFQs");
    }
  };

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: "#fdfdfd", minHeight: "100vh", paddingLeft: "clamp(15px, 5vw, 48px)", paddingRight: "clamp(15px, 5vw, 48px)" }}>
      <button
        className="btn btn-link p-0 mb-3 text-decoration-none fw-bold"
        style={{ color: colors.primary }}
        onClick={() => {
          clearCompare();
          navigate(-1);
        }}
      >
        ← Back to Products
      </button>

      <h2 className="fw-bold mb-1" style={{ color: colors.primary, letterSpacing: "-0.5px", fontSize: "clamp(24px, 4vw, 32px)" }}>Compare Products</h2>
      <p className="text-muted mb-4 mb-md-5" style={{ fontSize: "clamp(13px, 1.2vw, 14px)" }}>Compare up to 4 products side by side to find the best fit for your needs</p>

      {/* CHANGED: Dynamically apply table-responsive and hide horizontal overflow on desktop */}
      <div 
        className={`rounded-3 border shadow-sm bg-white ${isMobile ? "table-responsive" : ""}`} 
        style={{ 
          maxHeight: "75vh", 
          overflowY: "auto", 
          overflowX: isMobile ? "auto" : "hidden" // Strictly hide horizontal scroll on laptops
        }}
      >
        {/* CHANGED: Applied table-layout: fixed on desktop to strictly enforce column widths */}
        <table className="table m-0" style={{ width: "100%", tableLayout: isMobile ? "auto" : "fixed" }}>
          <thead>
            <tr>
              <th style={topLeftHeaderStyle}>
                Product Details
              </th>
              {itemsWithSpecs.map((item, i) => (
                <th key={i} className="p-2 p-md-3 border-start bg-white" style={{ width: isMobile ? "160px" : "20%", minWidth: isMobile ? "160px" : "0", position: "relative", verticalAlign: "top" }}>
                  
                  <div
                    className="mb-3 w-100"
                    style={{
                      height: isMobile ? 120 : 180,
                      backgroundColor: item.main_image ? "#f8f6f2" : "#e15449",
                      border: "1px solid #e5e0d8",
                      borderRadius: "12px",
                      boxShadow: "inset 0 0 0 1px #f1ede6, 0 2px 8px rgba(0,0,0,0.04)",
                      position: "relative",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {item.main_image ? (
                      <img
                        src={`https://aaeternitas.com/uploads/products/${item.main_image}`}
                        alt="product"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          imageRendering: "auto",
                          padding: "8px"
                        }}
                      />
                    ) : (
                      <span className="text-white fw-bold" style={{ fontSize: "14px" }}>
                        No Image
                      </span>
                    )}

                    <div
                      className="position-absolute top-0 end-0 m-2 bg-white rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                      style={{ width: "24px", height: "24px", cursor: "pointer", zIndex: 10, border: "1px solid #e5e7eb" }}
                      onClick={() => {
                        removeFromCompare(item.listed_product_id);
                        if (items.length <= 1) {
                          navigate(-1);
                        }
                      }}
                    >
                      <span style={{ fontSize: "10px", fontWeight: "bold", color: "#ef4444" }}>✕</span>
                    </div>
                  </div>

                  <div style={{ padding: "0 4px" }}>
                    <span
                      className="badge rounded-pill mb-2 text-white text-wrap text-start"
                      style={{ backgroundColor: colors.primary, fontSize: "9px", padding: "5px 10px", lineHeight: 1.2 }}
                    >
                      {item.category_name || "Warehousing Equipment"}
                    </span>
                    <h6 className="fw-bold mb-1" style={{ color: colors.primary, fontSize: isMobile ? "12px" : "15px", whiteSpace: "normal" }}>{item.product_name}</h6>
                    <div className="d-flex flex-wrap align-items-center gap-1 mb-1">
                      <span className="fw-bold" style={{ fontSize: "11px", color: colors.primary }}>{item.business_name}</span>
                      <span
                        style={{
                          color: colors.success,
                          fontSize: "10px",
                          border: `1px solid ${colors.success}`,
                          borderRadius: "20px",
                          padding: "1px 8px",
                          whiteSpace: "nowrap",
                          fontWeight: "600"
                        }}
                      >
                        ✓ Verified
                      </span>
                    </div>
                    <div className="text-muted" style={{ fontSize: "11px" }}>
                      📍 {item.city || "N/A"} | 🏭 Manufacturer
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            <tr>
              <td style={rowHeaderStyle}>Rating</td>
              {itemsWithSpecs.map((item, i) => (
                <td key={i} style={dataCellStyle} className="border-start">
                  <span className="text-warning">★</span>{" "}
                  <span className="fw-bold text-dark">{Number(item.rating || 4.8).toFixed(1)}</span>
                  <span className="ms-1" style={{ fontSize: "12px" }}>({item.review_count || 0} reviews)</span>
                </td>
              ))}
            </tr>

            <tr>
              <td style={rowHeaderStyle}>Min. Orders</td>
              {itemsWithSpecs.map((item, i) => (
                <td key={i} style={dataCellStyle} className="border-start text-dark">10 units</td>
              ))}
            </tr>

            <tr>
              <td style={rowHeaderStyle}>Lead Time</td>
              {itemsWithSpecs.map((item, i) => (
                <td key={i} style={dataCellStyle} className="border-start text-dark">2 - 3 Weeks</td>
              ))}
            </tr>

            <tr>
              <td colSpan={itemsWithSpecs.length + 1} style={sectionHeaderStyle}>
                Technical Specifications
              </td>
            </tr>

            {getAllSpecs(itemsWithSpecs).map((specLabel, idx) => (
              <tr key={idx}>
                <td style={rowHeaderStyle}>{specLabel}</td>
                {itemsWithSpecs.map((item, i) => {
                  const spec = item.specs?.find(s => s.spec_label === specLabel);
                  return (
                    <td key={i} style={dataCellStyle} className="border-start text-dark">
                      {spec ? spec.value : "—"}
                    </td>
                  );
                })}
              </tr>
            ))}

            <tr>
              <td style={rowHeaderStyle}>Actions</td>
              {itemsWithSpecs.map((item, i) => (
                <td key={i} style={dataCellStyle} className="border-start">
                  <div className="d-flex flex-column gap-2 py-2">
                    <button
                      className="btn text-white btn-sm fw-bold w-100"
                      style={{ backgroundColor: colors.primary, borderRadius: "6px", fontSize: "12px", padding: "10px" }}
                      onClick={() => handleSingleRFQ(item)}
                    >
                      Send RFQ
                    </button>
                    <button
                      className="btn btn-outline-dark btn-sm fw-bold w-100"
                      style={{ borderRadius: "6px", fontSize: "12px", padding: "10px", borderColor: colors.border }}
                      onClick={() => navigate(`/product/${item.listed_product_id}`)}
                    >
                      View Details
                    </button>
                  </div>
                </td>
              ))}
            </tr>
            <tr>
              <td colSpan={itemsWithSpecs.length + 1} className="text-center py-4 bg-white" style={{ position: "sticky", left: 0 }}>
                <button
                  className="btn fw-bold"
                  style={{
                    backgroundColor: colors.primary,
                    color: "white",
                    borderRadius: "6px",
                    padding: "12px 28px",
                    fontSize: "13px"
                  }}
                  onClick={handleBulkRFQ}
                >
                  Send RFQ to All
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ComparePage;